import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, ReferenceLine } from 'recharts';
import { aiApi, AI_WS_URL } from '../api';
import { useWebSocket } from '../hooks/useWebSocket';
import Market from './Market';
import Trades from './Trades';

// Painel (fase 2 da reorganizacao de UX — ver vault Claude/Specs).
// Una tela pra responder "como esta tudo agora": preco ao vivo, KPIs,
// grafico com sinais (embeda Market), ranking, sinais fortes, saude dos
// modelos e o diario manual (recolhivel). Analise profunda de IA continua
// em /insights (fora do menu), linkada no card de IA.

const card = { background: '#1e293b', borderRadius: 12, padding: 20, marginBottom: 16 };
const grid3 = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 12, marginBottom: 16 };

function StatCard({ label, value, sub, color = '#38bdf8', small }) {
  return (
    <div style={{ ...card, borderLeft: `3px solid ${color}`, padding: '14px 18px', marginBottom: 0 }}>
      <div style={{ fontSize: 11, color: '#64748b', marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: small ? 20 : 26, fontWeight: 800, color, lineHeight: 1 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

function LivePrice({ symbol, color }) {
  // Preco via websocket (mesma conexao que o Market usa por simbolo) com
  // fallback pro ticker REST; compartilha a queryKey ['ticker', symbol].
  const { data: tick } = useWebSocket(`${AI_WS_URL}/ws/prices/${symbol}`);
  const { data: ticker } = useQuery({
    queryKey: ['ticker', symbol],
    queryFn: () => aiApi.get(`/ticker/${symbol}`).then(r => r.data),
    refetchInterval: 30_000,
  });
  const price = tick?.price ?? parseFloat(ticker?.lastPrice ?? 0);
  const change = parseFloat(ticker?.priceChangePercent ?? 0);
  if (!price) return null;
  return (
    <StatCard
      label={symbol.replace('USDT', '/USDT')}
      value={`$${price.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
      sub={<span style={{ color: change >= 0 ? '#34d399' : '#f87171' }}>{change >= 0 ? '+' : ''}{change.toFixed(2)}% 24h</span>}
      color={color}
    />
  );
}

function SignalChip({ signal, confidence, symbol, interval }) {
  const color = signal === 'buy' ? '#34d399' : signal === 'sell' ? '#f87171' : '#64748b';
  const icon = signal === 'buy' ? '▲' : signal === 'sell' ? '▼' : '—';
  return (
    <div style={{ background: `${color}18`, border: `1px solid ${color}44`, borderRadius: 8, padding: '8px 12px', minWidth: 110 }}>
      <div style={{ fontSize: 11, color: '#64748b', marginBottom: 2 }}>{symbol} {interval}</div>
      <div style={{ fontSize: 14, fontWeight: 800, color }}>{icon} {signal?.toUpperCase()}</div>
      <div style={{ fontSize: 11, color: '#94a3b8' }}>{Math.round((confidence || 0) * 100)}% conf</div>
    </div>
  );
}

function BotRow({ bot, rank }) {
  const medals = { 1: '🥇', 2: '🥈', 3: '🥉' };
  const pnlColor = bot.pnl >= 0 ? '#34d399' : '#f87171';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid #1e293b' }}>
      <span style={{ fontSize: 18, width: 28 }}>{medals[rank] || `#${rank}`}</span>
      <span style={{ fontSize: 16 }}>{bot.emoji}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: 13 }}>{bot.name}</div>
        <div style={{ fontSize: 10, color: '#64748b' }}>{bot.symbol || 'BTC'} · {bot.interval} · {bot.leverage}x</div>
      </div>
      <div style={{ textAlign: 'right' }}>
        <div style={{ fontWeight: 800, fontSize: 14, color: pnlColor }}>{bot.pnl >= 0 ? '+' : ''}{bot.pnl?.toFixed(2)}%</div>
        <div style={{ fontSize: 10, color: '#64748b' }}>{bot.total_trades} trades · {bot.win_rate?.toFixed(0)}% win</div>
      </div>
    </div>
  );
}

// Card de IA: direcao MTF do simbolo + treino rapido + link pra analise completa
function IaCard({ symbol }) {
  const { data: mtf } = useQuery({
    queryKey: ['mtf-predict', symbol],
    queryFn: () => aiApi.get(`/predict/mtf/${symbol}`).then(r => r.data),
    refetchInterval: 120_000,
  });
  const trainMtf = useMutation({
    mutationFn: () => aiApi.post(`/train/mtf/${symbol}?days=365&days_1m=7`),
  });
  const sig = mtf?.signal || mtf?.prediction?.signal;
  const conf = mtf?.confidence ?? mtf?.prediction?.confidence;
  const color = sig === 'buy' ? '#34d399' : sig === 'sell' ? '#f87171' : '#94a3b8';
  return (
    <div style={{ ...card, marginBottom: 0 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: '#94a3b8', marginBottom: 10 }}>🧠 Direção MTF · {symbol.replace('USDT', '')}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 10 }}>
        <span style={{ fontSize: 24, fontWeight: 900, color }}>
          {sig ? (sig === 'buy' ? '▲ COMPRA' : sig === 'sell' ? '▼ VENDA' : '— NEUTRO') : '—'}
        </span>
        {conf != null && <span style={{ fontSize: 16, fontWeight: 700, color: '#e2e8f0' }}>{Math.round(conf * 100)}%</span>}
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button onClick={() => trainMtf.mutate()} disabled={trainMtf.isPending} style={{
          background: '#1e293b', color: trainMtf.isPending ? '#475569' : '#38bdf8',
          border: '1px solid #334155', borderRadius: 8, padding: '7px 12px',
          cursor: 'pointer', fontSize: 12, fontWeight: 600,
        }}>{trainMtf.isPending ? 'Treinando MTF...' : '⚙️ Treinar MTF'}</button>
        <Link to="/insights" style={{
          background: 'transparent', color: '#64748b', border: '1px solid #334155',
          borderRadius: 8, padding: '7px 12px', cursor: 'pointer', fontSize: 12,
          textDecoration: 'none', display: 'inline-block',
        }}>Análise completa →</Link>
      </div>
      {trainMtf.isError && (
        <div style={{ fontSize: 11, color: '#fbbf24', marginTop: 8 }}>
          Falha no treino — veja os logs do ai_service.
        </div>
      )}
    </div>
  );
}

export default function Painel() {
  const [journalOpen, setJournalOpen] = useState(false);

  const { data: arena } = useQuery({
    queryKey: ['arena-dash'],
    queryFn: () => aiApi.get('/arena').then(r => r.data),
    refetchInterval: 15_000,
  });
  const { data: signals } = useQuery({
    queryKey: ['signals-dash'],
    queryFn: () => aiApi.get('/signals/watch?threshold=0.60').then(r => r.data),
    refetchInterval: 30_000,
  });
  const { data: modelsHealth } = useQuery({
    queryKey: ['models-dash'],
    queryFn: () => aiApi.get('/models/health').then(r => r.data),
    refetchInterval: 120_000,
  });
  const { data: botStatus } = useQuery({
    queryKey: ['bot-dash'],
    queryFn: () => aiApi.get('/bot/status').then(r => r.data),
    refetchInterval: 15_000,
  });
  const { data: sentiment } = useQuery({
    queryKey: ['sentiment'],
    queryFn: () => aiApi.get('/sentiment').then(r => r.data),
    refetchInterval: 60_000,
  });

  const bots = arena?.bots || [];
  const top3 = [...bots].sort((a, b) => b.pnl_pct - a.pnl_pct).slice(0, 5);
  const totalPnl = bots.reduce((s, b) => s + (b.pnl || 0), 0);
  const totalTrades = bots.reduce((s, b) => s + (b.total_trades || 0), 0);
  const activeBots = bots.filter(b => b.positions?.length > 0).length;

  const allModels = modelsHealth?.models || [];
  const trainedModels = allModels.filter(m => m.status === 'trained');
  const avgF1 = trainedModels.length
    ? trainedModels.reduce((s, m) => s + (m.metrics?.f1_macro || 0), 0) / trainedModels.length
    : 0;

  const userPnlPct = botStatus?.pnl_pct || 0;
  const alertSignals = signals?.alerts || [];
  const fg = sentiment?.fear_greed_index;

  const equityData = (() => {
    if (!bots.length) return [];
    const allPoints = {};
    bots.forEach(b => {
      (b.equity_history || []).forEach(pt => {
        allPoints[pt.time] = (allPoints[pt.time] || 0) + pt.value;
      });
    });
    const times = Object.keys(allPoints).sort();
    if (!times.length) return [];
    const base = allPoints[times[0]];
    return times.map(t => ({
      time: new Date(t * 1000).toLocaleDateString('pt-BR', { month: 'short', day: 'numeric' }),
      value: parseFloat(((allPoints[t] / base - 1) * 100).toFixed(2)),
    }));
  })();

  return (
    <div>
      {/* Precos ao vivo + KPIs */}
      <div style={{ ...grid3 }}>
        <LivePrice symbol="BTCUSDT" color="#f7931a" />
        <LivePrice symbol="ETHUSDT" color="#627eea" />
        <StatCard label="Sentimento" value={fg != null ? fg : '—'}
          color={(sentiment?.bias) === 'risk_off' ? '#f87171' : '#34d399'}
          sub={sentiment?.bias === 'risk_on' ? 'Risco ligado' : sentiment?.bias === 'risk_off' ? 'Risco desligado' : 'Neutro'} />
        <StatCard label="Arena PnL total" value={`${totalPnl >= 0 ? '+' : ''}$${totalPnl.toFixed(2)}`}
          color={totalPnl >= 0 ? '#34d399' : '#f87171'}
          sub={`${bots.length} bots · ${totalTrades} trades`} />
        <StatCard label="Bots com posição" value={activeBots} color="#a78bfa" sub={`de ${bots.length} ativos`} />
        <StatCard label="Modelos treinados" value={`${trainedModels.length}/${allModels.length}`}
          color={avgF1 >= 0.4 ? '#34d399' : '#fbbf24'} sub={`F1 médio: ${(avgF1 * 100).toFixed(0)}%`} />
        <StatCard label="Paper Trading" value={`${userPnlPct >= 0 ? '+' : ''}${userPnlPct.toFixed(2)}%`}
          color={userPnlPct >= 0 ? '#34d399' : '#f87171'} sub={`saldo: $${(botStatus?.capital || 0).toFixed(2)}`} />
      </div>

      {/* Grafico + sinais (Market completo, autocontido) */}
      <Market />

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
        {/* Ranking */}
        <div style={card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: '#94a3b8' }}>🏆 Ranking da Arena</span>
            <Link to="/bots?mode=arena" style={{ fontSize: 11, color: '#38bdf8', textDecoration: 'none' }}>abrir Arena →</Link>
          </div>
          {top3.length === 0
            ? <div style={{ color: '#475569', fontSize: 12, padding: '12px 0' }}>Nenhum bot rodando.</div>
            : top3.map((b, i) => <BotRow key={b.id} bot={b} rank={i + 1} />)}
        </div>

        {/* Equity */}
        <div style={card}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#94a3b8', marginBottom: 12 }}>📈 Equity combinada (%)</div>
          {equityData.length >= 2 ? (
            <ResponsiveContainer width="100%" height={160}>
              <LineChart data={equityData} margin={{ top: 4, bottom: 0, left: -20, right: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e3a5f" />
                <XAxis dataKey="time" stroke="#334155" tick={{ fontSize: 9 }} interval="preserveStartEnd" />
                <YAxis stroke="#334155" tick={{ fontSize: 9 }} />
                <ReferenceLine y={0} stroke="#334155" strokeDasharray="4 2" />
                <Tooltip contentStyle={{ background: '#0f172a', border: '1px solid #334155', fontSize: 11 }}
                  formatter={(v) => [`${v >= 0 ? '+' : ''}${v}%`, 'PnL']} />
                <Line type="monotone" dataKey="value" stroke="#38bdf8" dot={false} strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ color: '#475569', fontSize: 12, padding: '20px 0', textAlign: 'center' }}>
              Sem histórico de equity ainda.
            </div>
          )}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 16, marginBottom: 16 }}>
        {/* Sinais fortes */}
        <div style={card}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#94a3b8', marginBottom: 12 }}>
            ⚡ Sinais fortes agora (conf ≥ 60%)
            {alertSignals.length > 0 && (
              <span style={{ marginLeft: 8, background: '#fbbf24', color: '#0f172a', borderRadius: 10, padding: '1px 6px', fontSize: 10, fontWeight: 800 }}>
                {alertSignals.length}
              </span>
            )}
          </div>
          {alertSignals.length === 0 ? (
            <div style={{ color: '#475569', fontSize: 12 }}>Nenhum sinal forte no momento.</div>
          ) : (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {alertSignals.map(a => (
                <SignalChip key={a.key} signal={a.signal} confidence={a.confidence} symbol={a.symbol} interval={a.interval} />
              ))}
            </div>
          )}
        </div>

        {/* IA: MTF + treino + link */}
        <IaCard symbol="BTCUSDT" />
      </div>

      {/* Saude dos modelos */}
      {trainedModels.length > 0 && (
        <div style={card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: '#94a3b8' }}>🧠 Saúde dos modelos</span>
            <Link to="/models" style={{ fontSize: 11, color: '#38bdf8', textDecoration: 'none' }}>gerenciar →</Link>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {trainedModels.map(m => {
              const f1 = m.metrics?.f1_macro || 0;
              const buyF1 = m.metrics?.buy_f1 || 0;
              const color = f1 >= 0.45 ? '#34d399' : f1 >= 0.35 ? '#fbbf24' : '#f87171';
              return (
                <div key={m.key} style={{ background: '#0f172a', borderRadius: 8, padding: '8px 12px', border: `1px solid ${color}44` }}>
                  <div style={{ fontSize: 11, color: '#64748b', marginBottom: 2 }}>{m.symbol} {m.interval}</div>
                  <div style={{ fontSize: 13, fontWeight: 700, color }}>F1 {(f1 * 100).toFixed(0)}%</div>
                  <div style={{ fontSize: 10, color: buyF1 < 0.2 ? '#f87171' : '#64748b' }}>buy {(buyF1 * 100).toFixed(0)}%</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Diario manual — recolhido por padrao */}
      <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
        <button onClick={() => setJournalOpen(o => !o)} style={{
          width: '100%', background: 'transparent', border: 'none', color: '#94a3b8',
          padding: '14px 20px', cursor: 'pointer', fontSize: 13, fontWeight: 700,
          textAlign: 'left', display: 'flex', justifyContent: 'space-between',
        }}>
          <span>📒 Diário de operações manuais</span>
          <span style={{ color: '#475569' }}>{journalOpen ? '▲' : '▼'}</span>
        </button>
        {journalOpen && (
          <div style={{ padding: '0 20px 20px' }}>
            <Trades embedded />
          </div>
        )}
      </div>
    </div>
  );
}
